import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, DmarcPanel, Field as KitField, NumberField, SelectField, StatusPill, Toggle, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

// The three per-domain mail tabs the admin console never had: Settings, Spam and Routing.
//
// Eleven endpoints — all domain_admin, all implemented, none reachable from v2. The domain detail
// screen offered Mailboxes, Aliases and DKIM and stopped there, so per-domain rate limits, the SNI
// certificate, transport and relay overrides, the whole spam policy with its inheritance, the
// curated filtering relaxations, and tenant-owned relays and routes could only be changed by curl.

interface Settings {
  domain: string;
  helo_identity?: string;
  tls_cert_file: string;
  tls_key_file: string;
  transport: string;
  relayhost: string;
  rate_hourly: number;
  rate_daily: number;
}

// GET/PUT /domains/{domain}/sogo — SOGo serves every hosted mail domain unless the operator takes one
// out. A separate record on purpose: switching the domain's webmail to Roundcube does not opt it out.
interface DomainSogo {
  domain: string;
  enabled: boolean;
  served: boolean;
  sogo_is_webmail: boolean;
  note?: string;
}

function SogoOptOut({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const KEY = ["domain-sogo", domain];
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => client.apiFetch<DomainSogo>(`/domains/${encodeURIComponent(domain)}/sogo`),
    retry: false,
  });
  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      client.apiFetch<unknown>(`/domains/${encodeURIComponent(domain)}/sogo`, {
        method: "PUT",
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: (_d, enabled) => {
      notify({ kind: "success", title: enabled ? "SOGo switched on for this domain" : "SOGo switched off for this domain" });
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ["support-services"] });
    },
    onError: (e) => notify({ kind: "error", title: "Could not change SOGo", body: e instanceof Error ? e.message : String(e) }),
  });
  const v = q.data;
  return (
    <Section
      title="SOGo groupware"
      className="mt-6"
      actions={
        v ? (
          <StatusPill tone={v.served ? "ok" : v.enabled ? "warn" : "neutral"}>
            {v.served ? "serving" : v.enabled ? "not a mail domain" : "switched off"}
          </StatusPill>
        ) : undefined
      }
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="SOGo for this domain">
        <div className="max-w-2xl">
          <Toggle
            checked={!!v?.enabled}
            disabled={save.isPending || !v || (v.enabled && v.sogo_is_webmail)}
            onChange={(on) => save.mutate(on)}
            label="SOGo authenticates this domain's mailboxes"
            help={
              v?.enabled && v.sogo_is_webmail
                ? "SOGo is this domain's webmail, so it cannot be switched off here — choose Roundcube as its webmail first."
                : "Every hosted mail domain is in SOGo unless switched off here. Choosing Roundcube as the domain's webmail does not switch SOGo off."
            }
            consequence={v?.enabled ? "Its mailboxes can no longer sign in to SOGo (calendars, contacts, webmail at /sogo) on any node." : undefined}
          />
          {v?.note && <p className="mt-2 text-[11.5px] leading-relaxed text-muted">{v.note}</p>}
        </div>
      </StateView>
    </Section>
  );
}

export function DomainSettingsTab({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const KEY = ["mail-domain-settings", domain];
  const base = `/mail/domains/${encodeURIComponent(domain)}`;

  const q = useQuery({ queryKey: KEY, queryFn: () => client.apiFetch<Settings>(`${base}/settings`), retry: false });
  const [draft, setDraft] = React.useState<Settings | null>(null);
  const cur = draft ?? q.data ?? null;
  const set = (k: keyof Settings, v: string | number) => cur && setDraft({ ...cur, [k]: v } as Settings);

  const save = useMutation({
    mutationFn: (s: Settings) =>
      client.apiFetch<unknown>(`${base}/settings`, {
        method: "PUT",
        body: JSON.stringify({
          tls_cert_file: s.tls_cert_file, tls_key_file: s.tls_key_file,
          transport: s.transport, relayhost: s.relayhost,
          rate_hourly: Number(s.rate_hourly) || 0, rate_daily: Number(s.rate_daily) || 0,
        }),
      }),
    onSuccess: () => {
      notify({ kind: "success", title: "Mail settings saved" });
      setDraft(null);
      void qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (e) => notify({ kind: "error", title: "Could not save", body: e instanceof Error ? e.message : String(e) }),
  });

  // The URL is written out at each call site rather than assembled from a variable. Same reason
  // the migration cancel is two explicit calls: a path built from a fragment is invisible to the
  // parity extractor, so a route that IS called gets reported unreachable — and it is harder to
  // read besides.
  const act = (label: string, call: Promise<unknown>) =>
    call
      .then(() => {
        notify({ kind: "success", title: label });
        void qc.invalidateQueries({ queryKey: KEY });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  // Binds a settings key; the markup is the kit primitive's.
  const Field = ({ k, label, ph, hint, type }: { k: keyof Settings; label: string; ph?: string; hint?: string; type?: string }) => (
    <div className="max-w-lg py-1.5">
      <KitField
        label={label}
        value={String(cur?.[k] ?? "")}
        placeholder={ph}
        help={hint}
        type={type === "password" ? "password" : "text"}
        onChange={(v) => set(k, type === "number" ? Number(v) : v)}
      />
    </div>
  );

  return (
    <StateView loading={q.isLoading} error={q.error} errorContext="this domain's mail settings">
      <Section title="Identity">
        {/* Read-only ON PURPOSE. MailDomainSettings records why: a per-domain HELO was stored and
            shown and nothing ever generated Postfix config from it, and it could not work anyway —
            one address has one PTR, so a per-tenant HELO fails forward-confirmed reverse DNS for
            every tenant but one. Showing it as a fact rather than a field is the honest version. */}
        <div className="text-[12px] text-muted">This node announces itself as</div>
        <div className="mt-1 font-mono text-[13.5px]">{cur?.helo_identity || "—"}</div>
        <div className="mt-2 max-w-2xl text-[11.5px] leading-relaxed text-muted">
          HELO identifies the machine, not the domain — one address has one reverse-DNS name, so it
          cannot differ per tenant. What identifies this domain is the From header, the DKIM
          signature and the envelope sender's SPF, which is what DMARC checks.
        </div>
      </Section>

      <Section
        title="Certificate for mail (SNI)"
        className="mt-6"
        actions={<Button size="sm" onClick={() => void act("Certificate taken from the website", client.apiFetch(`${base}/tls/from-caddy`, { method: "POST" }))}>Use the website's certificate</Button>}
      >
        <Field k="tls_cert_file" label="Certificate file" ph="/etc/ozpanel/mailcerts/example.com.crt" />
        <Field k="tls_key_file" label="Private key file" ph="/etc/ozpanel/mailcerts/example.com.key" />
        <div className="max-w-2xl text-[11.5px] leading-relaxed text-muted">
          Mail clients connecting for this domain are served this certificate. Both fields are set
          together or left empty together — the server refuses one without the other, because half a
          pair silently falls back to the node's own certificate and the client sees the wrong name.
        </div>
      </Section>

      <Section title="Where this domain's mail goes" className="mt-6">
        <Field k="transport" label="Transport" ph="smtp:[mail.elsewhere.com]:25"
          hint="Overrides delivery for this domain. Leave empty for normal local delivery." />
        <Field k="relayhost" label="Relay host" ph="[smtp.provider.com]:587"
          hint="Sends this domain's OUTBOUND mail through another server instead of delivering it directly." />
      </Section>

      <Section title="Sending limits" className="mt-6">
        <div className="flex flex-wrap gap-4">
          <Field k="rate_hourly" label="Messages per hour" type="number" />
          <Field k="rate_daily" label="Messages per day" type="number" />
        </div>
        <div className="max-w-2xl text-[11.5px] leading-relaxed text-muted">
          0 means no limit. A limit here is what stops one compromised mailbox getting the whole
          node's address on a blocklist.
        </div>
      </Section>

      <Section title="DNS" className="mt-6"
        actions={<Button size="sm" onClick={() => void act("Mail DNS records published", client.apiFetch(`${base}/dns`, { method: "POST" }))}>Publish mail records</Button>}>
        <div className="max-w-2xl text-[12px] leading-relaxed text-muted">
          Writes this domain's MX, SPF, DKIM and DMARC records into the zone we host for it. Safe to
          run again — it reconciles rather than duplicating.
        </div>
      </Section>

      <div className="mt-6">
        <DmarcPanel domain={domain} />
      </div>

      <SogoOptOut domain={domain} />

      <div className="mt-6 flex items-center gap-3">
        <Button disabled={!draft || save.isPending} onClick={() => draft && save.mutate(draft)}>
          {save.isPending ? "Saving…" : "Save settings"}
        </Button>
        {draft && <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>Discard</Button>}
      </div>
    </StateView>
  );
}

interface ListEntry { id: number | string; value: string; mailbox?: string }
interface Relaxation {
  key: string; title: string; enabled: boolean; explain?: string;
  // A STRING ("off"), not a flag — spam_relax.go calls it "the setting we advise". Declared as a
  // boolean it was truthy for every relaxation, so every one was badged "recommended".
  recommended?: string; recommend_why?: string; tradeoff?: string; symbols?: string[];
}
interface SpamView {
  domain: string;
  scope?: string;
  note?: string;
  policy?: { spam_score?: number; reject_score?: number; skip_greylist?: boolean };
  platform_defaults?: {
    spam_score?: number;
    reject_score?: number;
    // The greylisting DELAY in minutes, not a switch. Declared boolean it read as "on" for any
    // non-zero delay and "off" for a delay of 0 — which is the one value that means greylisting
    // is disabled, so the two states it most needed to tell apart were the two it confused.
    greylist?: number;
    note?: string;
    source?: string;
    // The RANGES most sites land in, per setting — {greylist: "2 to 6", reject_score: "12 to 20",
    // …} — so a threshold can be judged against something. Declared `string` and rendered
    // directly it is an object handed to React as a child, which takes the tab down.
    typical?: { greylist?: string; reject_score?: string; spam_score?: string } | null;
  };
  allow?: ListEntry[] | null;
  block?: ListEntry[] | null;
  mailbox_overrides?: { mailbox: string; spam_score?: number; reject_score?: number }[] | null;
}

export function DomainSpamTab({ domain }: { domain: string }) {
  const { client } = useAuth();
  // What the scanner has actually SEEN for this domain, beside the policy that governs it. A
  // threshold with no traffic behind it is a setting nobody can judge.
  const stats = useQuery({
    queryKey: ["rspamd-domain-stats", domain],
    queryFn: () =>
      client.apiFetch<{
        scanned?: number; filtered?: number; average_score?: number;
        direction?: { inbound?: { scanned?: number; filtered?: number }; outbound?: { scanned?: number; filtered?: number } };
        note?: string;
      }>(`/rspamd/domains/${encodeURIComponent(domain)}/stats`),
    retry: false,
  });
  const { notify } = useToast();
  const qc = useQueryClient();
  const base = `/mail/domains/${encodeURIComponent(domain)}`;
  const KEY = ["mail-domain-spam", domain];
  const RKEY = ["mail-domain-relax", domain];

  const spam = useQuery({ queryKey: KEY, queryFn: () => client.apiFetch<SpamView>(`${base}/spam`), retry: false });
  const relax = useQuery({
    queryKey: RKEY,
    queryFn: () => client.apiFetch<{ relaxations: Relaxation[] | null; note?: string }>(`${base}/spam/relax`),
    retry: false,
  });

  const d: SpamView = spam.data ?? ({ domain } as SpamView);
  const pol = d.policy ?? {};
  const def = d.platform_defaults ?? {};
  const [draft, setDraft] = React.useState<{ spam_score: string; reject_score: string; skip_greylist: boolean } | null>(null);
  const cur = draft ?? {
    spam_score: pol.spam_score != null ? String(pol.spam_score) : "",
    reject_score: pol.reject_score != null ? String(pol.reject_score) : "",
    skip_greylist: !!pol.skip_greylist,
  };

  const [adding, setAdding] = React.useState<{ list_type: "allow" | "block"; value: string } | null>(null);
  const [removing, setRemoving] = React.useState<ListEntry | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: RKEY });
  };
  const run = (label: string, p: Promise<unknown>) =>
    p.then(() => { notify({ kind: "success", title: label }); setDraft(null); refresh(); })
     .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  const listCols = (): DataListColumn<ListEntry>[] => [
    { key: "value", label: "Address or domain", sortable: true, render: (x) => <span className="font-mono text-[12.5px]">{x.value}</span> },
    { key: "mailbox", label: "Applies to", render: (x) => x.mailbox || <span className="text-muted">the whole domain</span> },
  ];

  return (
    <div>
      <Section title="What the filter has seen">
        <StateView loading={stats.isLoading} error={stats.error} errorContext="this domain's mail statistics">
          <div className="flex flex-wrap gap-5 text-[13px]">
            <span>Received <span className="font-mono">{stats.data?.direction?.inbound?.scanned ?? 0}</span></span>
            <span>Sent <span className="font-mono">{stats.data?.direction?.outbound?.scanned ?? 0}</span></span>
            <span className={(stats.data?.direction?.inbound?.filtered ?? 0) > 0 ? "text-warn" : "text-muted"}>
              Filtered in <span className="font-mono">{stats.data?.direction?.inbound?.filtered ?? 0}</span>
            </span>
            {/* Outbound filtering means one of this domain's own mailboxes is sending spam —
                the opposite problem, and the one that costs the domain its reputation. */}
            <span className={(stats.data?.direction?.outbound?.filtered ?? 0) > 0 ? "text-crit" : "text-muted"}>
              Filtered out <span className="font-mono">{stats.data?.direction?.outbound?.filtered ?? 0}</span>
            </span>
            <span className="text-muted">
              Average score <span className="font-mono">{stats.data?.average_score != null ? stats.data.average_score.toFixed(1) : "—"}</span>
            </span>
          </div>
          {stats.data?.note && <p className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">{stats.data.note}</p>}
        </StateView>
      </Section>

      <StateView loading={spam.isLoading} error={spam.error} errorContext="this domain's spam policy">
        <Section title="How strict to be" className="mt-6">
          {/* THE INTERLOCK. An empty box here does not mean "no filtering", it means "follow the
              level above" — and without saying what that level IS, a blank field reads as off.
              The server sends platform_defaults precisely so the page can state the inherited
              value rather than implying there is none. */}
          <div className="mb-3 max-w-2xl rounded-lg border border-line bg-surface2 px-3 py-2 text-[12px] leading-relaxed text-muted">
            Left empty, this domain follows the platform default:{" "}
            <span className="text-fg">flag at {def.spam_score ?? "—"}</span>,{" "}
            <span className="text-fg">refuse at {def.reject_score ?? "—"}</span>, greylisting{" "}
            {/* The DELAY, said out loud. "on" hid the one number that decides whether
                greylisting inconveniences a sender for two minutes or for six, and 0 — which
                means off — was the value most likely to be misread as on. */}
            <span className="text-fg">
              {def.greylist ? `${def.greylist} minute${def.greylist === 1 ? "" : "s"}` : "off"}
            </span>
            {def.typical?.greylist || def.typical?.spam_score || def.typical?.reject_score ? (
              <>
                {" "}· typical sites use{" "}
                <span className="text-fg">
                  {[
                    def.typical.spam_score ? `flag at ${def.typical.spam_score}` : "",
                    def.typical.reject_score ? `refuse at ${def.typical.reject_score}` : "",
                    def.typical.greylist ? `${def.typical.greylist} minutes of greylisting` : "",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-4">
            <NumberField
              label="Move to Junk at"
              value={cur.spam_score}
              placeholder={String(def.spam_score ?? "")}
              onChange={(v) => setDraft({ ...cur, spam_score: v })}
            />
            <NumberField
              label="Refuse outright at"
              value={cur.reject_score}
              placeholder={String(def.reject_score ?? "")}
              onChange={(v) => setDraft({ ...cur, reject_score: v })}
            />
          </div>
          <label className="mt-3 flex cursor-pointer items-start gap-2 text-[13px]">
            <input type="checkbox" className="mt-0.5" checked={cur.skip_greylist} onChange={(e) => setDraft({ ...cur, skip_greylist: e.target.checked })} />
            <span>
              Skip greylisting for this domain
              <span className="mt-0.5 block text-[11.5px] text-muted">
                Greylisting refuses a first-time sender once and accepts the retry, which stops most
                bulk spam but delays the first message from any new contact. Turn it off where that
                delay matters more than the spam.
              </span>
            </span>
          </label>
          <div className="mt-4">
            <Button disabled={!draft} onClick={() => void run("Spam policy saved", client.apiFetch(`${base}/spam`, {
              method: "PUT",
              body: JSON.stringify({
                spam_score: cur.spam_score === "" ? null : Number(cur.spam_score),
                reject_score: cur.reject_score === "" ? null : Number(cur.reject_score),
                skip_greylist: cur.skip_greylist,
              }),
            }))}>Save policy</Button>
            {draft && <Button variant="ghost" size="sm" className="ml-2" onClick={() => setDraft(null)}>Discard</Button>}
          </div>
        </Section>

        {(d.mailbox_overrides?.length ?? 0) > 0 && (
          <Section title="Mailboxes with their own settings" className="mt-6" bodyClassName="p-0">
            <div className="p-4 text-[12px] text-muted">
              These override the domain policy above, so a change here does not affect them.
            </div>
            <div className="px-4 pb-4">
              <ul className="space-y-1 text-[13px]">
                {(d.mailbox_overrides ?? []).map((o: { mailbox: string; spam_score?: number; reject_score?: number }) => (
                  <li key={o.mailbox} className="font-mono">
                    {o.mailbox}
                    <span className="ml-2 font-sans text-[12px] text-muted">
                      junk at {o.spam_score ?? "—"}, refuse at {o.reject_score ?? "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Section>
        )}

        {(["allow", "block"] as const).map((kind) => (
          <Section
            key={kind}
            className="mt-6"
            bodyClassName="p-0"
            title={kind === "allow" ? "Always accept" : "Always refuse"}
            actions={<Button size="sm" onClick={() => setAdding({ list_type: kind, value: "" })}>Add</Button>}
          >
            <div className="px-4 pt-3 text-[12px] text-muted">
              {kind === "allow"
                ? "Mail from these senders skips the spam score entirely — use it for a sender that keeps being filtered wrongly."
                : "Mail from these senders is refused regardless of its score."}
            </div>
            <div className="p-4">
              <DataList
                items={(kind === "allow" ? d.allow : d.block) ?? []}
                columns={listCols()}
                getRowKey={(x) => String(x.id)}
                emptyMessage={kind === "allow" ? "Nothing is always-accepted." : "Nothing is always-refused."}
                rowActions={(x) => (
                  <Button size="sm" variant="danger" onClick={() => setRemoving(x)}>Remove</Button>
                )}
              />
            </div>
          </Section>
        ))}
      </StateView>

      <Section title="Filtering relaxations" className="mt-6">
        <StateView loading={relax.isLoading} error={relax.error} errorContext="the filtering relaxations">
          <div className="mb-3 max-w-2xl text-[12px] leading-relaxed text-muted">
            {relax.data?.note ?? "Each of these stops one signal counting against mail sent to this domain."}
          </div>
          <div className="space-y-3">
            {(relax.data?.relaxations ?? []).map((r) => (
              <div key={r.key} className="max-w-3xl rounded-lg border border-line p-3">
                <label className="flex cursor-pointer items-start gap-2.5">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={r.enabled}
                    onChange={(e) =>
                      void run(e.target.checked ? `${r.title} relaxed` : `${r.title} enforced again`,
                        client.apiFetch(`${base}/spam/relax/${encodeURIComponent(r.key)}`, {
                          method: "PUT",
                          body: JSON.stringify({ enabled: e.target.checked }),
                        }))
                    }
                  />
                  <span className="min-w-0">
                    <span className="text-[13px]">
                      {r.title}
                      {r.recommended === "off" && <span className="ml-2"><StatusPill tone="ok">we advise off</StatusPill></span>}
                    </span>
                    {r.explain && <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">{r.explain}</span>}
                    {/* Both sides, always. A relaxation that only states its benefit is one the
                        reader cannot judge — every one of these trades a bit of filtering away. */}
                    {r.tradeoff && <span className="mt-1 block text-[11.5px] leading-relaxed text-warn">Trade-off: {r.tradeoff}</span>}
                    {r.recommend_why && (
                      <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">{r.recommend_why}</span>
                    )}
                  </span>
                </label>
              </div>
            ))}
          </div>
        </StateView>
      </Section>

      <ConfirmDialog
        open={!!adding}
        onOpenChange={(o) => !o && setAdding(null)}
        title={adding?.list_type === "allow" ? "Always accept mail from" : "Always refuse mail from"}
        confirmLabel="Add"
        body={
          <div>
            <input
              autoFocus
              value={adding?.value ?? ""}
              placeholder="someone@example.com or example.com"
              onChange={(e) => setAdding(adding ? { ...adding, value: e.target.value } : null)}
              className="w-full rounded-lg border border-line2 bg-bg px-3 py-2 font-mono text-[13px] text-fg outline-none focus:border-accent"
            />
            <p className="mt-2 text-[12px] text-muted">A whole domain matches every address at it.</p>
          </div>
        }
        onConfirm={() => {
          const a = adding;
          setAdding(null);
          if (!a?.value.trim()) return;
          void run("Added", client.apiFetch(`${base}/spam/lists`, {
            method: "POST",
            body: JSON.stringify({ list_type: a.list_type, value: a.value.trim(), mailbox: "" }),
          }));
        }}
      />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.value ?? ""}?`}
        confirmLabel="Remove"
        destructive
        body={<p>Mail from this sender goes back to being scored normally.</p>}
        onConfirm={() => {
          const x = removing;
          setRemoving(null);
          if (x) void run("Removed", client.apiFetch(`${base}/spam/lists/${encodeURIComponent(String(x.id))}`, { method: "DELETE" }));
        }}
      />
    </div>
  );
}

interface Relay {
  id: number;
  name: string;
  nexthop: string;
  auth_enabled?: boolean;
  username?: string;
  tls_level?: string;
  has_password?: boolean;
}
interface Route {
  id: number;
  scope?: string;
  localpart?: string;
  relay_id?: number;
  relay_name?: string;
  destination_domain?: string;
}

export function DomainRoutingTab({ domain }: { domain: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const base = `/mail/domains/${encodeURIComponent(domain)}`;
  const KEY = ["mail-domain-routing", domain];

  const q = useQuery({
    queryKey: KEY,
    queryFn: () => client.apiFetch<{ relays: Relay[] | null; routes: Route[] | null }>(`${base}/routing`),
    retry: false,
  });
  const relays = q.data?.relays ?? [];
  const routes = q.data?.routes ?? [];

  const [newRelay, setNewRelay] = React.useState<{ name: string; nexthop: string; username: string; password: string; tls_level: string } | null>(null);
  const [newRoute, setNewRoute] = React.useState<{ scope: string; localpart: string; relay_id: string } | null>(null);
  const [removing, setRemoving] = React.useState<{ what: string; path: string } | null>(null);

  const run = (label: string, p: Promise<unknown>) =>
    p.then(() => { notify({ kind: "success", title: label }); void qc.invalidateQueries({ queryKey: KEY }); })
     .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  const relayCols: DataListColumn<Relay>[] = [
    { key: "name", label: "Name", sortable: true },
    { key: "nexthop", label: "Sends to", render: (r) => <span className="font-mono text-[12.5px]">{r.nexthop}</span> },
    {
      key: "auth", label: "Sign-in",
      render: (r) =>
        r.auth_enabled ? (
          <span className="text-[12px]">
            {r.username || "—"}
            {/* A relay with auth ON and no password stored authenticates with nothing and every
                message through it is refused. That is not a detail: it is the failure, named. */}
            {r.has_password === false && <span className="ml-2 text-crit">no password stored</span>}
          </span>
        ) : (
          <span className="text-muted">none</span>
        ),
    },
    { key: "tls_level", label: "TLS", render: (r) => r.tls_level || <span className="text-muted">default</span> },
  ];

  const routeCols: DataListColumn<Route>[] = [
    {
      key: "scope", label: "Applies to", sortable: true,
      render: (r) => (r.scope === "mailbox" && r.localpart ? <span className="font-mono text-[12.5px]">{r.localpart}@{domain}</span> : <span>the whole domain</span>),
    },
    {
      key: "relay", label: "Goes through",
      render: (r) => r.relay_name || relays.find((x) => x.id === r.relay_id)?.name || <span className="text-muted">relay {r.relay_id}</span>,
    },
  ];

  return (
    <div>
      <StateView loading={q.isLoading} error={q.error} errorContext="this domain's routing">
        <Section
          title="Relays"
          bodyClassName="p-0"
          actions={<Button size="sm" onClick={() => setNewRelay({ name: "", nexthop: "", username: "", password: "", tls_level: "encrypt" })}>Add relay</Button>}
        >
          <div className="px-4 pt-3 text-[12px] leading-relaxed text-muted">
            A server this domain's outbound mail is handed to instead of being delivered directly —
            an ISP's smarthost, or a sending service. The password is encrypted at rest and never
            shown again.
          </div>
          <div className="p-4">
            <DataList
              items={relays} columns={relayCols} getRowKey={(r) => String(r.id)}
              emptyMessage="No relays — mail is delivered directly."
              rowActions={(r) => (
                <Button size="sm" variant="danger" onClick={() => setRemoving({ what: `the relay "${r.name}"`, path: `${base}/relays/${r.id}` })}>Remove</Button>
              )}
            />
          </div>
        </Section>

        <Section
          title="Routes"
          className="mt-6"
          bodyClassName="p-0"
          actions={
            <Button size="sm" disabled={relays.length === 0} onClick={() => setNewRoute({ scope: "domain", localpart: "", relay_id: String(relays[0]?.id ?? "") })}>
              Add route
            </Button>
          }
        >
          <div className="px-4 pt-3 text-[12px] leading-relaxed text-muted">
            Which mail goes through which relay. Without a route a relay is configured and unused —
            {relays.length === 0 ? " add a relay first." : " a relay with no route changes nothing."}
          </div>
          <div className="p-4">
            <DataList
              items={routes} columns={routeCols} getRowKey={(r) => String(r.id)}
              emptyMessage="No routes."
              rowActions={(r) => (
                <Button size="sm" variant="danger" onClick={() => setRemoving({ what: "that route", path: `${base}/routes/${r.id}` })}>Remove</Button>
              )}
            />
          </div>
        </Section>
      </StateView>

      <ConfirmDialog
        open={!!newRelay}
        onOpenChange={(o) => !o && setNewRelay(null)}
        title="New relay"
        confirmLabel="Create"
        body={
          <div className="space-y-2">
            {([["name", "Name", "smarthost"], ["nexthop", "Server", "[smtp.provider.com]:587"], ["username", "Sign in as", "optional"], ["password", "Password", "optional"]] as const).map(([k, label, ph]) => (
              <KitField
                key={k}
                mono
                label={label}
                type={k === "password" ? "password" : "text"}
                value={String(newRelay?.[k] ?? "")}
                placeholder={ph}
                onChange={(v) => setNewRelay(newRelay ? { ...newRelay, [k]: v } : null)}
              />
            ))}
            <SelectField
              label="Encryption"
              value={newRelay?.tls_level ?? "encrypt"}
              onChange={(v) => setNewRelay(newRelay ? { ...newRelay, tls_level: v } : null)}
              options={[
                { value: "may", label: "may — encrypt if offered" },
                { value: "encrypt", label: "encrypt — required" },
                { value: "verify", label: "verify — required, certificate checked" },
                { value: "implicit", label: "implicit — port 465, TLS from the first byte" },
              ]}
            />
            <p className="text-[11.5px] text-muted">
              Square brackets around the host stop Postfix looking up its MX record, which is what
              you want for a smarthost. A provider that only takes port 465 (Cloudflare Email Service:
              [smtp.mx.cloudflare.net]:465, user api_token) needs "implicit".
            </p>
          </div>
        }
        onConfirm={() => {
          const r = newRelay;
          setNewRelay(null);
          if (!r?.name.trim() || !r.nexthop.trim()) return;
          void run("Relay created", client.apiFetch(`${base}/relays`, {
            method: "POST",
            body: JSON.stringify({
              name: r.name.trim(), nexthop: r.nexthop.trim(),
              auth_enabled: !!r.username.trim(),
              username: r.username.trim(), password: r.password,
              tls_level: r.tls_level,
            }),
          }));
        }}
      />

      <ConfirmDialog
        open={!!newRoute}
        onOpenChange={(o) => !o && setNewRoute(null)}
        title="New route"
        confirmLabel="Create"
        body={
          <div className="space-y-2">
            <SelectField
              label="Applies to"
              value={newRoute?.scope ?? "domain"}
              onChange={(v) => setNewRoute(newRoute ? { ...newRoute, scope: v } : null)}
              options={[
                { value: "domain", label: "the whole domain" },
                { value: "mailbox", label: "one mailbox" },
              ]}
            />
            {newRoute?.scope === "mailbox" && (
              <KitField
                mono
                label="Mailbox"
                value={newRoute?.localpart ?? ""}
                placeholder="sales"
                onChange={(v) => setNewRoute(newRoute ? { ...newRoute, localpart: v } : null)}
              />
            )}
            <label className="block text-[12px] text-muted">
              Through
              <select
                value={newRoute?.relay_id ?? ""}
                onChange={(e) => setNewRoute(newRoute ? { ...newRoute, relay_id: e.target.value } : null)}
                className="mt-1 w-full rounded-lg border border-line2 bg-bg px-3 py-1.5 text-[13px] text-fg"
              >
                {relays.map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
              </select>
            </label>
          </div>
        }
        onConfirm={() => {
          const r = newRoute;
          setNewRoute(null);
          if (!r) return;
          void run("Route created", client.apiFetch(`${base}/routes`, {
            method: "POST",
            // relay_id is a NUMBER. Sent as the string the <select> holds, the server rejects it —
            // this exact mismatch is already on the record in this codebase.
            body: JSON.stringify({
              scope: r.scope,
              localpart: r.scope === "mailbox" ? r.localpart.trim() : "",
              relay_id: Number(r.relay_id),
            }),
          }));
        }}
      />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.what ?? ""}?`}
        confirmLabel="Remove"
        destructive
        body={<p>Mail that was using it goes back to normal delivery from the next message.</p>}
        onConfirm={() => {
          const x = removing;
          setRemoving(null);
          if (x) void run("Removed", client.apiFetch(x.path, { method: "DELETE" }));
        }}
      />
    </div>
  );
}
